/**
 * Your data: imports with preview and reconciliation, full export and restore, sharing and access,
 * partner exchanges and traceability evidence. Imported history files into other modules' event
 * types (or core notes) with source_kind "import", so this module defines no events of its own.
 */
import { defineModule, f } from "../../contract";

export type { ImportDTO, ImportPreview, ImportReport, ExchangeDTO, ShareDTO, EvidencePacket, ProgramDecision } from "./shared/types";

export default defineModule({
  id: "data",
  label: "Your data",
  description: "Imports, exports, sharing, partner exchanges and traceability.",
  attributes: {
    place: {
      aliases: f.text("Also called", { level: "more", help: "Other names people use for this place, separated by commas. Hondo understands them in notes and imports." }),
    },
  },
  jobs: [
    {
      type: "data.program",
      label: "Verification program",
      description: "Gather the evidence a buyer or program needs for a group of cattle, send it, and keep the provider's decision.",
      icon: "shield",
      tone: "info",
      workspace: "data",
      subjectKind: "animal",
      fields: {
        program: f.text("Program", { required: true, help: "The program's own name, like a source and age program." }),
        provider_id: f.ref("Provider", "contact", { help: "Who checks the evidence and decides." }),
        claims: f.longText("What it checks", { help: "Source, age, no added hormones, preconditioning, weaning date…" }),
        version: f.text("Rule set or version", { level: "more" }),
        from: f.date("Evidence from", { level: "more" }),
        to: f.date("Evidence to", { level: "more" }),
      },
      stages: [
        { id: "preparing", label: "Gathering evidence" },
        { id: "submitted", label: "Sent to the provider" },
        { id: "decided", label: "Provider decided" },
      ],
      agent: { when: "The rancher wants to enroll a group of cattle in a verification or marketing program (source and age, natural, preconditioned) or gather evidence for one." },
    },
  ],
  workspaces: [
    {
      id: "data",
      label: "Records and data",
      description: "Bring in spreadsheets and old records, download everything, send files to partners and prepare evidence packets.",
      icon: "data",
      group: "data",
      keywords: ["import", "export", "spreadsheet", "excel", "csv", "backup", "restore", "download", "cattlemax", "hondo v1", "traceability", "evidence", "verification", "source and age", "partner", "feedlot file", "share"],
      keywordsEs: ["importar", "exportar", "hoja de cálculo", "excel", "respaldo", "restaurar", "descargar", "cattlemax", "trazabilidad", "evidencia", "verificación", "origen y edad", "socio", "compartir"],
      permission: "data",
    },
  ],
  ranchSections: [
    {
      id: "data",
      label: "Your data",
      description: "Bring in records, settle what doesn't match, and download everything.",
      icon: "data",
      group: "data",
      keywords: ["import", "export", "backup", "spreadsheet", "cattlemax", "download"],
      keywordsEs: ["importar", "exportar", "respaldo", "hoja de cálculo", "descargar"],
      permission: "data",
    },
    {
      id: "access",
      label: "Access and sharing",
      description: "Who sees what, links you've shared, devices and recent activity.",
      icon: "shield",
      group: "people",
      keywords: ["access", "permissions", "share", "vet link", "audit", "sessions", "devices", "webhook"],
      keywordsEs: ["acceso", "permisos", "compartir", "enlace", "auditoría", "sesiones", "dispositivos"],
      permission: "manage",
    },
  ],
  messages: [
    // Column outcomes and row outcomes, shown from data.
    "Exact", "Translated", "Kept as written", "Not brought in", "Check this",
    "New", "Updated", "No change", "Differs", "Repeated", "Needs a look", "Skipped", "Kept in file",
    // Import families and statuses.
    "Cattle", "Places", "Records", "Hondo V1 cattle list", "Hondo V1 ranch log", "Hondo V1 tasks", "Not sure", "Hondo backup",
    "Ready to review", "Filing", "Filed", "Undone", "Couldn't read", "Put aside",
    // Exchange states and partner kinds.
    "Prepared", "Sent", "Accepted", "Partly accepted", "Rejected", "Outcome unknown", "Replaced",
    "Association", "Lab", "Sensor or device", "Feedlot or buyer", "Program", "Other",
    // Share categories.
    "Identity", "Health", "Breeding", "Weights", "Moves", "Arrivals and departures", "Genetics", "Dairy", "Notes",
    // Program decisions.
    "Enrolled", "Partly enrolled", "Not accepted", "Expired",
    // Where a column can go (shared/targets.ts).
    "Source record ID", "Keep as written", "Don't bring in", "Tag", "Name", "EID", "Registration number", "Sex", "Castrated", "Birth date", "Breed", "Color",
    "Status", "Origin", "Notes", "Dam", "Sire", "Dam's source record ID", "Sire's source record ID", "Pasture or pen", "Group", "Unit for this row's amounts",
    "Place name", "Also called", "Kind of place", "Area", "Capacity (head)", "Animal's tag", "Date", "Record",
    "About the animal", "Family", "Where", "About the place", "Other details", "Other",
    "day first", "month first", "year first", "An animal", "A place", "Not sure, leave it in the file", "in this file", "in your herd", "not found",
    "Spreadsheet", "Archive", "Document", "Photo", "PDF", "Data file", "Other file", "Empty", "Can't read",
    "Accepted with changes", "Waiting", "Turned off", "Finished", "Cancelled", "Paused",
    "Gathering evidence", "Sent to the provider", "Provider decided",
    // Downloads one list at a time.
    "Every record", "Tasks", "Groups", "Group membership history", "Tags, EIDs and registrations", "People and businesses", "Equipment", "Supplies", "Supply ins and outs", "Jobs",
    "Supply lots", "Job worklists", "Financial records",
    // What each role may do.
    "See cattle, places, work and history", "Record work and notes", "File anyone's notes", "Change records, jobs and settings", "See finances", "Change finances",
    "Invite crew, share records, see activity", "Bring in files and download everything",
    // Activity words.
    "Added", "Changed", "Corrected", "Removed", "Merged", "Uploaded files to bring in", "Started filing an import", "Filed an import", "Undid an import", "Settled an import question",
    "Set an import aside", "Downloaded an original file", "Sent a file to Hondo", "Started restoring a backup", "Restored a backup", "Downloaded everything", "Downloaded a saved export",
    "Downloaded a list", "Downloaded an evidence packet", "Shared records", "Turned off a shared link", "Opened a shared link", "Downloaded from a shared link", "Connected a device",
    "Made a new device key", "Turned off a device key", "A device sent a note", "Signed someone out", "Signed everyone else out", "Prepared a partner file", "Marked a partner file sent",
    "Recorded a partner's answer", "Downloaded a partner file", "Recorded a program decision",
    "Everything", "Shared links", "Downloads", "Imports", "Devices", "Sign-ins", "Removed records", "Corrections",
    // Partner file adapters.
    "Evidence packet (CSV)", "IDs, birth dates, origins, moves and treatments with withdrawal status for each animal, for a program or a receiving buyer.",
    "Animal list (CSV)", "Tag, EID, sex, birth date and breed for each animal, in a spreadsheet any partner can open.",
    // Section label on the animal page.
    "Where this came from",
  ],
});
